/**
 * Compliance Audit Report
 * 
 * Business-friendly dashboard for viewing PII data changes
 * for compliance reporting and monitoring.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Collapse,
  alpha,
  styled,
  Autocomplete,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  LocalHospital as MedicalIcon,
  AccountBalance as FinancialIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import {
  getComplianceAuditLogs,
  getAuditUsers,
  exportAuditLogs,
  formatFieldName,
  formatTimestamp,
  maskSensitiveValue,
  PiiAuditLogEntry,
  AuditQueryResult,
  AuditUser,
  FieldCategory,
  AuditAction,
  EntityType,
  FIELD_CATEGORY_DISPLAY,
  ACTION_DISPLAY,
  ENTITY_TYPE_DISPLAY,
  FIELD_CATEGORY_COLORS,
  ACTION_COLORS,
} from '../../services/complianceAuditService';

// Styled components
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
}));

const SummaryCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  textAlign: 'center',
  borderRadius: '12px',
  transition: 'transform 0.2s, box-shadow 0.2s',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const ValueChangeCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const OldValue = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.error.main, 0.1),
  color: theme.palette.error.dark,
  padding: '4px 8px',
  borderRadius: '4px',
  fontSize: '0.8rem',
  textDecoration: 'line-through',
  wordBreak: 'break-word',
}));

const NewValue = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.success.main, 0.1),
  color: theme.palette.success.dark,
  padding: '4px 8px',
  borderRadius: '4px',
  fontSize: '0.8rem',
  wordBreak: 'break-word',
}));

const ComplianceAuditReport: React.FC = () => {
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AuditQueryResult | null>(null);
  const [users, setUsers] = useState<AuditUser[]>([]);
  const [showFilters, setShowFilters] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Filter state
  const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [selectedUser, setSelectedUser] = useState<AuditUser | null>(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [entityType, setEntityType] = useState<EntityType | ''>('');
  const [fieldCategory, setFieldCategory] = useState<FieldCategory | ''>('');
  const [action, setAction] = useState<AuditAction | ''>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Load users for filter dropdown
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userList = await getAuditUsers();
        setUsers(userList);
      } catch (err) {
        console.error('Error loading users:', err);
      }
    };
    loadUsers();
  }, []);

  // Fetch audit logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getComplianceAuditLogs({
        startDate: startOfDay(startDate),
        endDate: endOfDay(endDate),
        userId: selectedUser?.id,
        patientId: patientSearch || undefined,
        entityType: entityType || undefined,
        fieldCategory: fieldCategory || undefined,
        action: action || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      setResult(data);
    } catch (err: any) {
      console.error('Error fetching audit logs:', err);
      setError(err.message || 'Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedUser, patientSearch, entityType, fieldCategory, action, page, rowsPerPage]);

  // Initial load and refresh on filter changes
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Handle export
  const handleExport = async () => {
    setExporting(true);
    try {
      await exportAuditLogs({
        startDate: startOfDay(startDate),
        endDate: endOfDay(endDate),
        userId: selectedUser?.id,
        patientId: patientSearch || undefined,
        entityType: entityType || undefined,
        fieldCategory: fieldCategory || undefined,
        action: action || undefined,
      });
    } catch (err: any) {
      console.error('Error exporting:', err);
      setError('Failed to export audit logs');
    } finally {
      setExporting(false);
    }
  };

  // Handle pagination
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Clear filters
  const clearFilters = () => {
    setStartDate(subDays(new Date(), 30));
    setEndDate(new Date());
    setSelectedUser(null);
    setPatientSearch('');
    setEntityType('');
    setFieldCategory('');
    setAction('');
    setPage(0);
  };

  // Handle summary card click to filter
  const handleCategoryClick = (category: FieldCategory) => {
    setFieldCategory(category);
    setPage(0);
  };

  // Get category icon
  const getCategoryIcon = (category: FieldCategory) => {
    switch (category) {
      case 'BASIC_PII':
        return <PersonIcon />;
      case 'MEDICAL':
        return <MedicalIcon />;
      case 'FINANCIAL':
        return <FinancialIcon />;
      default:
        return <SecurityIcon />;
    }
  };

  // Render action chip
  const renderActionChip = (actionType: AuditAction) => (
    <Chip
      size="small"
      label={ACTION_DISPLAY[actionType]}
      sx={{
        backgroundColor: alpha(ACTION_COLORS[actionType], 0.15),
        color: ACTION_COLORS[actionType],
        fontWeight: 600,
      }}
    />
  );

  // Render category chip
  const renderCategoryChip = (category: FieldCategory) => (
    <Chip
      size="small"
      icon={getCategoryIcon(category)}
      label={FIELD_CATEGORY_DISPLAY[category]}
      sx={{
        backgroundColor: alpha(FIELD_CATEGORY_COLORS[category], 0.15),
        color: FIELD_CATEGORY_COLORS[category],
        fontWeight: 500,
        '& .MuiChip-icon': {
          color: 'inherit',
        },
      }}
    />
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Compliance Audit Report
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Track and monitor all PII data changes for compliance
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={fetchLogs}
                disabled={loading}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                onClick={handleExport}
                disabled={exporting || loading}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                }}
              >
                Export CSV
              </Button>
            </Box>
          </Box>
        </CardContent>
      </HeaderCard>

      {/* Summary Cards */}
      {result && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <SummaryCard onClick={() => { setFieldCategory(''); setPage(0); }}>
              <SecurityIcon sx={{ fontSize: 32, color: '#667eea', mb: 1 }} />
              <Typography variant="h4" fontWeight={600}>
                {result.summary.totalChanges}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Changes
              </Typography>
            </SummaryCard>
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard
              onClick={() => handleCategoryClick('BASIC_PII')}
              sx={{
                borderLeft: `4px solid ${FIELD_CATEGORY_COLORS.BASIC_PII}`,
                bgcolor: fieldCategory === 'BASIC_PII' ? alpha(FIELD_CATEGORY_COLORS.BASIC_PII, 0.05) : undefined,
              }}
            >
              <PersonIcon sx={{ fontSize: 32, color: FIELD_CATEGORY_COLORS.BASIC_PII, mb: 1 }} />
              <Typography variant="h4" fontWeight={600}>
                {result.summary.byCategory.BASIC_PII || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Basic PII
              </Typography>
            </SummaryCard>
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard
              onClick={() => handleCategoryClick('MEDICAL')}
              sx={{
                borderLeft: `4px solid ${FIELD_CATEGORY_COLORS.MEDICAL}`,
                bgcolor: fieldCategory === 'MEDICAL' ? alpha(FIELD_CATEGORY_COLORS.MEDICAL, 0.05) : undefined,
              }}
            >
              <MedicalIcon sx={{ fontSize: 32, color: FIELD_CATEGORY_COLORS.MEDICAL, mb: 1 }} />
              <Typography variant="h4" fontWeight={600}>
                {result.summary.byCategory.MEDICAL || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Medical Records
              </Typography>
            </SummaryCard>
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard
              onClick={() => handleCategoryClick('FINANCIAL')}
              sx={{
                borderLeft: `4px solid ${FIELD_CATEGORY_COLORS.FINANCIAL}`,
                bgcolor: fieldCategory === 'FINANCIAL' ? alpha(FIELD_CATEGORY_COLORS.FINANCIAL, 0.05) : undefined,
              }}
            >
              <FinancialIcon sx={{ fontSize: 32, color: FIELD_CATEGORY_COLORS.FINANCIAL, mb: 1 }} />
              <Typography variant="h4" fontWeight={600}>
                {result.summary.byCategory.FINANCIAL || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Financial/Insurance
              </Typography>
            </SummaryCard>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ mb: 3, borderRadius: '12px' }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: showFilters ? '1px solid' : 'none',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Filters
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={clearFilters}>
              Clear All
            </Button>
            <IconButton size="small" onClick={() => setShowFilters(!showFilters)}>
              {showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>
        <Collapse in={showFilters}>
          <Box sx={{ p: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(date) => date && setStartDate(date)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(date) => date && setEndDate(date)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Autocomplete
                    size="small"
                    options={users}
                    getOptionLabel={(option) => option.name || option.email}
                    value={selectedUser}
                    onChange={(_, value) => setSelectedUser(value)}
                    renderInput={(params) => (
                      <TextField {...params} label="Changed By" placeholder="Select user" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Patient ID"
                    placeholder="Search by patient ID"
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={1.5}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={fieldCategory}
                      label="Category"
                      onChange={(e) => setFieldCategory(e.target.value as FieldCategory | '')}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="BASIC_PII">Basic PII</MenuItem>
                      <MenuItem value="MEDICAL">Medical</MenuItem>
                      <MenuItem value="FINANCIAL">Financial</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={1.5}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Entity Type</InputLabel>
                    <Select
                      value={entityType}
                      label="Entity Type"
                      onChange={(e) => setEntityType(e.target.value as EntityType | '')}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="PATIENT">Patient</MenuItem>
                      <MenuItem value="ENCOUNTER">Encounter</MenuItem>
                      <MenuItem value="ALLERGY">Allergy</MenuItem>
                      <MenuItem value="MEDICATION">Medication</MenuItem>
                      <MenuItem value="INSURANCE">Insurance</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={1}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Action</InputLabel>
                    <Select
                      value={action}
                      label="Action"
                      onChange={(e) => setAction(e.target.value as AuditAction | '')}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="CREATE">Created</MenuItem>
                      <MenuItem value="UPDATE">Updated</MenuItem>
                      <MenuItem value="DELETE">Deleted</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Box>
        </Collapse>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '12px' }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading audit logs...
          </Typography>
        </Paper>
      )}

      {/* Results Table */}
      {!loading && result && (
        <Paper sx={{ borderRadius: '12px' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Changed By</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Patient</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Value Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No audit logs found for the selected filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  result.logs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {formatTimestamp(log.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={log.userEmail || log.userId}>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {log.userName || 'Unknown User'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {log.userEmail || log.userId}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{renderActionChip(log.action)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.patientName || log.patientId || '—'}
                        </Typography>
                        {log.patientId && log.patientName && (
                          <Typography variant="caption" color="text.secondary">
                            ID: {log.patientId}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {formatFieldName(log.fieldName)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ENTITY_TYPE_DISPLAY[log.entityType]}
                        </Typography>
                      </TableCell>
                      <TableCell>{renderCategoryChip(log.fieldCategory)}</TableCell>
                      <TableCell sx={{ maxWidth: 250 }}>
                        <ValueChangeCell>
                          {log.action === 'DELETE' || log.oldValue ? (
                            <OldValue>
                              {log.fieldCategory === 'FINANCIAL'
                                ? maskSensitiveValue(log.oldValue, log.fieldCategory)
                                : log.oldValue || '(empty)'}
                            </OldValue>
                          ) : null}
                          {log.action !== 'DELETE' && (
                            <NewValue>
                              {log.fieldCategory === 'FINANCIAL'
                                ? maskSensitiveValue(log.newValue, log.fieldCategory)
                                : log.newValue || '(empty)'}
                            </NewValue>
                          )}
                        </ValueChangeCell>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={result.pagination.total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}
    </Box>
  );
};

export default ComplianceAuditReport;

